import _ from "lodash";
import io from "socket.io-client";
import React, { createContext, useRef, useState } from "react";
import { createNotification } from "react-redux-notify";
import { connect, useDispatch, useSelector } from "react-redux";

import {
  appendMessage,
  removeMessage,
  setChatFilters,
  sendMessageSuccess,
  setActiveConversation,
  fetchChatMessageSuccess,
  setActiveConversationLoading,
  setActiveConversationIfCurrent,
  sendMessageFailure,
} from "../../store/actions/ChatAction";
import { getUserId } from "../storage/user";
import { CommunicationEvents } from "../socket/events";
import { getChatroomMember } from "../socket/chatroom";
import { setUserChatRooms } from "../../store/actions/ChatAction";
import { setChatFollowers } from "../../store/actions/FollowAction";
import { userOnlineStatusChanged } from "../../store/actions/UserAction";
import { fetchWalletDetailsStart } from "../../store/actions/WalletAction";
import { ChatroomStatusEnum, SidebarListTypes } from "../constants/message";
import CommunicationsErrorHanlder from "../../helpers/services/communication/error";
import { getErrorNotificationMessage } from "../../components/helper/NotificationMessage";
import { getSuccessNotificationMessage } from "../../components/helper/NotificationMessage";
import { isAuthenticated } from "../authentication/authentication";
import { appConfig } from "../../config";

let socket;
let connectionTimeout;
let retryTimeout;

export const SocketContext = createContext({});
const url = appConfig.socketHost || "http://localhost:9000";

// Connection configuration
const CONNECTION_TIMEOUT = 10000; // 10 seconds
const MAX_RETRIES = 5;
const RETRY_DELAY = 2000; // 2 seconds base delay

function SocketProvider({ children }) {
  const dispatch = useDispatch()
  const [connected, setConnected] = useState(false);
  const retryCountRef = useRef(0);
  const [isConnecting, setIsConnecting] = useState(false);
  const chatrooms = useSelector((state) => state.chat.chatrooms)
  const [isLoading, setIsLoading] = useState(false)
  const pendingJoinRef = useRef(null);

  const clearTimeouts = () => {
    if (connectionTimeout) {
      clearTimeout(connectionTimeout);
      connectionTimeout = null;
    }
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
  };

  const connect = () => {
    const token = localStorage.getItem('jwtToken') || ''
    
    // Only attempt connection if user is authenticated
    if (!isAuthenticated()) {
      console.info('[Socket] User not authenticated, skipping connection attempt');
      return;
    }
    
    // Prevent multiple connection attempts
    if (socket && (socket.connected || isConnecting)) {
      console.info('[Socket] Connection already in progress or established');
      return;
    }

    // Clear any existing timeouts
    clearTimeouts();
    setIsConnecting(true);
    retryCountRef.current = 0;

    console.info('[Socket] Attempting connection...', { retryCount: retryCountRef.current });

    socket = io(`${url}?token=${token}`, {
      secure: true,
      transports: ["websocket"],
      timeout: CONNECTION_TIMEOUT,
      reconnection: false,
    });

    connectionTimeout = setTimeout(() => {
      console.info('[Socket] Connection timeout');
      handleConnectionFailure('Connection timeout');
    }, CONNECTION_TIMEOUT);

    socket.on('connect', () => {
      console.info('[Socket] Connected successfully');
      clearTimeouts();
      setConnected(true);
      setIsConnecting(false);
      retryCountRef.current = 0;
      // Try the pending join
      if (pendingJoinRef.current) {
        const { chatroom_id, initiated_by } = pendingJoinRef.current;
        console.info('[Socket] Retrying pending JOIN_CHATROOM');
        socket.emit(CommunicationEvents.JOIN_CHATROOM, {
          chatroom_id: _.toInteger(chatroom_id),
          initiated_by: _.toInteger(initiated_by),
        });
        pendingJoinRef.current = null; // clear it
      }
    });

    socket.on("error", (error) => {
      console.info('[Socket] Socket error:', error);
      handleConnectionFailure('Socket error');
    });

    socket.on("disconnect", (reason) => {
      console.info('[Socket] Disconnected:', reason);
      setConnected(false);
      setIsConnecting(false);
      
      // Only retry if it wasn't a manual disconnect
      if (reason !== 'io client disconnect') {
        handleConnectionFailure('Disconnected');
      }
    });

    socket.on("connect_error", (error) => {
      console.info('[Socket] Connection error:', error);
      handleConnectionFailure('Connection error');
    });

    socket.on(CommunicationEvents.TOAST_NOTIFICATION, (payload) => {
      const { type, message } = payload;
      dispatch(
        createNotification(
          type === "error"
            ? getErrorNotificationMessage(message)
            : getSuccessNotificationMessage(message)
        )
      );
    });

    socket.on(CommunicationEvents.USER_CHATROOMS, (payload) => {
      if (!_.isEqual(chatrooms.data, payload)) {
        dispatch(setUserChatRooms(payload));
      }
    });

    socket.on(CommunicationEvents.JOIN_CHATROOM_RESPONSE, (payload) => {
      dispatch(setActiveConversation({
        chatroom: payload,
        member: getChatroomMember(payload)
      }));
    });

    socket.on(CommunicationEvents.CHATROOM_MESSAGES, (payload) => {
      dispatch(fetchChatMessageSuccess(payload));
    });

    socket.on(CommunicationEvents.BROADCAST_MESSAGE, (payload) => {
      dispatch(appendMessage(payload));
    });

    socket.on(CommunicationEvents.SEND_MESSAGE_RESPONSE, (payload) => {
      setIsLoading(false);
      dispatch(sendMessageSuccess(payload));
    });

    socket.on(CommunicationEvents.ERROR, (payload) => {
      setIsLoading(false);
      dispatch(sendMessageFailure(payload));
    });

    socket.on(CommunicationEvents.SEND_BULK_MESSAGE_RESPONSE, (payload) => {
      dispatch(sendMessageSuccess(payload));
      dispatch(setChatFilters({
        sidebarListType: SidebarListTypes.Chatrooms
      }))
      if (payload.count) {
        dispatch(
          createNotification(
            getSuccessNotificationMessage(`Message sent to ${payload.count} users.`)
          )
        );
      }      
    });

    socket.on(CommunicationEvents.FETCH_FOLLOWERS_RESPONSE, (payload) => {
      dispatch(setChatFollowers(payload));
    });

    socket.on(CommunicationEvents.BROADCAST_DELETE_MESSAGE, (payload) => {
      dispatch(removeMessage(payload));
    });

    socket.on(
      CommunicationEvents.CHANGE_CHATROOM_VISIBILITY_RESPONSE,
      (payload) => {
        if (payload && payload.settings) {
          const status = payload.settings[getUserId()];
          if (status && !status.visible) {
            dispatch(
              createNotification(
                getSuccessNotificationMessage("Chatroom hidden!")
              )
            );
          }
          dispatch(setActiveConversationIfCurrent(payload));
        }
      }
    );

    socket.on(
      CommunicationEvents.DELETE_CHATROOM_RESPONSE,
      (payload) => {
        if (payload && payload.deleted_at) {
          dispatch(
            createNotification(
              getSuccessNotificationMessage("Coversation deleted!")
            )
          );
        }
      }
    );

    socket.on(
      CommunicationEvents.CHANGE_CHATROOM_NOTIFICATIONS_RESPONSE,
      (payload) => {
        if (payload && payload.settings) {
          const status = payload.settings[getUserId()];
          if (status) {
            dispatch(
              createNotification(
                getSuccessNotificationMessage(
                  status.notification ? "Chatroom unmuted!" : "Chatroom muted!"
                )
              )
            );
          }
          dispatch(setActiveConversationIfCurrent(payload));
        }
      }
    );

    socket.on(
      CommunicationEvents.CHANGE_CHATROOM_STATUS_RESPONSE,
      (payload) => {
        if (payload) {
          if (payload.status === ChatroomStatusEnum.Active) {
            dispatch(
              createNotification(
                getSuccessNotificationMessage("Chatroom unblocked!")
              )
            );
          }
          if (payload.status === ChatroomStatusEnum.Blocked) {
            dispatch(
              createNotification(
                getSuccessNotificationMessage("Chatroom blocked!")
              )
            );
          }
          dispatch(setActiveConversationIfCurrent(payload));
        }
      }
    );

    socket.on(CommunicationEvents.STATUS_CHANGED, (payload) => {
      dispatch(userOnlineStatusChanged(payload));
    });

    socket.on(CommunicationEvents.PAYMENT_COMPLETED, () => {
      dispatch(fetchWalletDetailsStart());
    });

    socket.on(CommunicationEvents.ERROR, (payload) => {
      const { error, tag } = payload;
      if (error) {
        console.log(`[${tag}]:`, error)
        const message = CommunicationsErrorHanlder.parseSocketError(error);
        dispatch(createNotification(getErrorNotificationMessage(message)));
      }
    });
  };

  const handleConnectionFailure = (reason) => {
    clearTimeouts();
    setConnected(false);
    setIsConnecting(false);
    
    // Only retry if user is authenticated
    if (!isAuthenticated()) {
      console.info('[Socket] User not authenticated, skipping retry');
      retryCountRef.current = 0;
      return;
    }

    if (retryCountRef.current < MAX_RETRIES) {
      const delay = RETRY_DELAY * Math.pow(2, retryCountRef.current);
      console.info(`[Socket] Connection failed: ${reason}. Retrying in ${delay}ms (attempt ${retryCountRef.current + 1}/${MAX_RETRIES})`);

      retryTimeout = setTimeout(() => {
        retryCountRef.current += 1; // Increment ref
        connect();
      }, delay);
    } else {
      console.info('[Socket] Max retries reached. Connection failed permanently.');
      dispatch(createNotification(getErrorNotificationMessage("Unable to connect to chat server. Please refresh the page.")));
      retryCountRef.current = 0;
    }
  };

  const disconnect = () => {
    if (socket) {
      socket.disconnect();
      socket = null;
    }
    clearTimeouts();
    setConnected(false);
    setIsConnecting(false);
    retryCountRef.current = 0;
  };

  // Function to handle authentication state changes
  const handleAuthStateChange = () => {
    if (!isAuthenticated()) {
      // User logged out, disconnect socket
      console.info('[Socket] User logged out, disconnecting socket');
      disconnect();
    } else if (!connected && !isConnecting) {
      // User logged in, attempt connection
      console.info('[Socket] User logged in, attempting connection');
      connect();
    }
  };

  const joinNotificationChatroom = (user_id) => {
    socket.emit(CommunicationEvents.JOIN_NOTIFICATION_CHATROOM, {
      user_id,
    });
  };

  const deleteMessage = (message_id) => {
    socket.emit(CommunicationEvents.DELETE_MESSAGE, { message_id });
  };

  const deleteChatroom = (chatroom_id) => {
    socket.emit(CommunicationEvents.DELETE_CHATROOM, { chatroom_id });
  };

  const joinChatroom = (chatroom_id, initiated_by) => {
    dispatch(setActiveConversationLoading(true))
    // Check if socket is connected before dispatching loading action
    if (!socket || !socket.connected) {
      pendingJoinRef.current = { chatroom_id, initiated_by };
      connect();
      return;
    }
    
    socket.emit(CommunicationEvents.JOIN_CHATROOM, {
      chatroom_id: _.toInteger(chatroom_id),
      initiated_by: _.toInteger(initiated_by)
    });
  };

  const sendMessage = (user_id, chatroom_id, text, assets = [], price = 0) => {
    setIsLoading(true);
    socket.emit(CommunicationEvents.SEND_MESSAGE, {
      text,
      price:Number(price),
      assets,
      user_id,
      chatroom_id,
      type: 'text',
      token: localStorage.getItem("jwtToken") || ""
    });
  };

  const sendBulkMessage = (user_ids, text, assets = [], price = 0) => {
    socket.emit(CommunicationEvents.SEND_BULK_MESSAGE, {
      text,
      price:Number(price),
      assets,
      user_ids,
      type: 'text',
      token: localStorage.getItem("jwtToken") || ""
    });
  };

  const fetchChatroomMessages = (chatroom_id, page) => {
    socket.emit(CommunicationEvents.FETCH_CHATROOM_MESSAGES, {
      chatroom_id,
      page,
    });
  };

  const changeChatroomStatus = (chatroom_id, status) => {
    socket.emit(CommunicationEvents.CHANGE_CHATROOM_STATUS, {
      status,
      chatroom_id,
    });
  };

  const changeChatroomVisibility = (chatroom_id, status) => {
    socket.emit(CommunicationEvents.CHANGE_CHATROOM_VISIBILITY, {
      status,
      chatroom_id,
    });
  };

  const changeChatroomNotification = (chatroom_id, status) => {
    socket.emit(CommunicationEvents.CHANGE_CHATROOM_NOTIFICATIONS, {
      status,
      chatroom_id,
    });
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        connect,
        disconnect,
        handleAuthStateChange,
        connected,
        isConnecting,
        retryCount: retryCountRef.current,
        isLoading,
        setIsLoading,
        sendMessage,
        setConnected,
        joinChatroom,
        deleteMessage,
        deleteChatroom,
        sendBulkMessage,
        changeChatroomStatus,
        fetchChatroomMessages,
        changeChatroomVisibility,
        joinNotificationChatroom,
        changeChatroomNotification,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

const mapStateToProps = (state) => ({
  chatrooms: state.chat.chatrooms,
  activeConversation: state.chat.activeConversation
});

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(mapStateToProps, mapDispatchToProps)(SocketProvider);
